import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, CloudDownload, AlertTriangle, CheckCircle2, FileSpreadsheet } from 'lucide-react';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { PageActions, PageTitle, PageToolbar } from '@/components/ui/PageToolbar';
import { api, apiError } from '@/lib/api-client';
import { useUiStore } from '@/stores/uiStore';

/**
 * Works & expenses sync from the Google Drive archive (SUPER_ADMIN).
 *
 * The archive holds one folder per forestry entity; this page shows how
 * each folder resolves against `entitate_silvica`, lets an admin settle
 * the ambiguous ones, and runs the same import the weekly job runs.
 */

type EntityRef = { cod: string; name: string };

type FolderMatch =
  | { folder: string; status: 'matched'; cod: string; name: string }
  | { folder: string; status: 'ambiguous'; candidates: EntityRef[] }
  | { folder: string; status: 'unmatched' };

type FolderPlan = {
  match: FolderMatch;
  folderId: string;
  sheet: { id: string; name: string; modifiedTime?: string } | null;
};

type EntitySyncResult = {
  folder: string;
  cod: string | null;
  status: 'imported' | 'skipped' | 'failed';
  reason?: string;
  parsed?: number;
  imported?: number;
  duplicates?: number;
};

type SyncRunResult = {
  startedAt: string;
  finishedAt: string;
  year: number;
  entities: EntitySyncResult[];
  totals: { imported: number; skipped: number; failed: number };
};

type StatusResponse = {
  configured: boolean;
  serviceAccountEmail: string | null;
  rootFolderId: string | null;
  keyError: string | null;
  lastRun: SyncRunResult | null;
};

type DiscoverResponse = {
  year: number;
  sheetTitle: string;
  rootFolderId: string;
  plans: FolderPlan[];
  entities: EntityRef[];
};

export function WorksSyncPage() {
  const qc = useQueryClient();
  const pushToast = useUiStore((s) => s.pushToast);
  const [overrides, setOverrides] = useState<Record<string, string>>({});

  const status = useQuery<StatusResponse>({
    queryKey: ['admin', 'works-sync', 'status'],
    queryFn: async () => api.get('admin/works-sync/status').json<StatusResponse>(),
  });

  const discover = useQuery<DiscoverResponse>({
    queryKey: ['admin', 'works-sync', 'discover'],
    queryFn: async () => api.get('admin/works-sync/discover').json<DiscoverResponse>(),
    enabled: status.data?.configured === true,
    retry: false,
  });

  const saveOverrides = useMutation({
    mutationFn: async (next: Record<string, string>) =>
      api.put('admin/works-sync/overrides', { json: { overrides: next } }).json(),
    onSuccess: async () => {
      pushToast({ variant: 'success', message: 'Asocierile au fost salvate' });
      await qc.invalidateQueries({ queryKey: ['admin', 'works-sync', 'discover'] });
    },
    onError: async (e) => pushToast({ variant: 'error', message: (await apiError(e)).message }),
  });

  const run = useMutation<SyncRunResult, unknown, { onlyFolders?: string[] }>({
    mutationFn: async (body) => api.post('admin/works-sync/run', { json: body }).json<SyncRunResult>(),
    onSuccess: async (r) => {
      pushToast({
        variant: r.totals.failed > 0 ? 'error' : 'success',
        message: `${r.totals.imported} rânduri importate · ${r.totals.skipped} omise · ${r.totals.failed} eșuate`,
      });
      await qc.invalidateQueries({ queryKey: ['admin', 'works-sync'] });
    },
    onError: async (e) => pushToast({ variant: 'error', message: (await apiError(e)).message }),
  });

  const plans = discover.data?.plans ?? [];
  const entities = discover.data?.entities ?? [];
  const ready = plans.filter((p) => p.match.status === 'matched' && p.sheet).length;
  const needsAttention = plans.filter(
    (p) => p.match.status !== 'matched' || !p.sheet,
  ).length;

  const setOverride = (folder: string, cod: string) => {
    const next = { ...overrides, [folder]: cod };
    if (!cod) delete next[folder];
    setOverrides(next);
  };

  return (
    <div className="animate-fade-in space-y-3">
      <PageToolbar>
        <PageTitle
          title="Sincronizare lucrări (Drive)"
          hint="Importă devizele de cheltuieli din arhiva Google Drive, o dată pe săptămână"
        />
        <PageActions>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => void discover.refetch()}
            disabled={!status.data?.configured || discover.isFetching}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${discover.isFetching ? 'animate-spin' : ''}`} />
            Reîmprospătează
          </Button>
          <Button
            size="sm"
            onClick={() => run.mutate({})}
            loading={run.isPending}
            disabled={!status.data?.configured || ready === 0}
          >
            <CloudDownload className="h-3.5 w-3.5" />
            Sincronizează tot ({ready})
          </Button>
        </PageActions>
      </PageToolbar>

      {/* Configuration ------------------------------------------------ */}
      <Card>
        <CardHeader className="px-3 py-2 text-sm font-semibold">Configurare</CardHeader>
        <CardBody className="space-y-2 px-3 py-2 text-sm">
          {status.isLoading ? (
            <Spinner />
          ) : status.data?.configured ? (
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
              <span className="inline-flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400">
                <CheckCircle2 className="h-4 w-4" /> Conectat
              </span>
              <span className="font-mono text-xs text-slate-600 dark:text-slate-400">
                {status.data.serviceAccountEmail}
              </span>
              <span className="font-mono text-xs text-slate-500">
                folder {status.data.rootFolderId}
              </span>
            </div>
          ) : (
            <div className="space-y-2 rounded border border-amber-300 bg-amber-50 p-3 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              <p className="flex items-center gap-1.5 font-semibold">
                <AlertTriangle className="h-4 w-4" /> Sincronizarea nu este configurată
              </p>
              {status.data?.keyError ? (
                <p className="font-mono text-xs">{status.data.keyError}</p>
              ) : null}
              <ol className="list-decimal space-y-1 pl-5 text-xs">
                <li>Creați un cont de serviciu Google cu Drive API activat.</li>
                <li>
                  Proprietarul arhivei partajează folderul PNERP_24 cu adresa contului de
                  serviciu (drept de vizualizare).
                </li>
                <li>
                  Setați <code className="font-mono">GOOGLE_SERVICE_ACCOUNT_JSON</code> și{' '}
                  <code className="font-mono">WORKS_DRIVE_FOLDER_ID</code>.
                </li>
              </ol>
            </div>
          )}

          {status.data?.lastRun ? (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Ultima rulare:{' '}
              {new Date(status.data.lastRun.finishedAt).toLocaleString('ro-MD')} —{' '}
              {status.data.lastRun.totals.imported} importate,{' '}
              {status.data.lastRun.totals.skipped} omise, {status.data.lastRun.totals.failed}{' '}
              eșuate (anul {status.data.lastRun.year})
            </p>
          ) : null}
        </CardBody>
      </Card>

      {/* Folder → entity mapping -------------------------------------- */}
      {status.data?.configured ? (
        <Card className="flex flex-col">
          <CardHeader className="flex items-center justify-between px-3 py-2">
            <span className="text-sm font-semibold">
              Foldere ({plans.length}) · fișa {discover.data?.sheetTitle ?? '—'}
            </span>
            {needsAttention > 0 ? (
              <Badge tone="yellow">{needsAttention} necesită atenție</Badge>
            ) : null}
          </CardHeader>
          <CardBody className="overflow-auto p-0">
            {discover.isLoading ? (
              <div className="p-6">
                <Spinner />
              </div>
            ) : discover.isError ? (
              <p className="p-4 text-sm text-red-700">
                {String((discover.error as Error)?.message ?? 'Eroare')}
              </p>
            ) : (
              <table className="table-striped w-full text-sm">
                <thead className="bg-slate-50/95 text-xs uppercase text-slate-500 dark:bg-slate-800/95">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Folder Drive</th>
                    <th className="px-3 py-2 text-left font-semibold">Entitate silvică</th>
                    <th className="px-3 py-2 text-left font-semibold">Fișă an curent</th>
                    <th className="px-3 py-2 text-right font-semibold">Acțiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {plans.map((p) => {
                    const folder = p.match.folder;
                    const matched = p.match.status === 'matched' ? p.match : null;
                    return (
                      <tr key={folder} className="border-t border-slate-100">
                        <td className="px-3 py-1.5 font-mono text-xs">{folder}</td>
                        <td className="px-3 py-1.5">
                          {matched ? (
                            <span>
                              <span className="font-mono text-xs text-slate-500">
                                {matched.cod}
                              </span>{' '}
                              {matched.name}
                            </span>
                          ) : (
                            // Ambiguous or unknown: the admin picks once and the
                            // weekly job stops guessing.
                            <Select
                              uiSize="sm"
                              aria-label={`Entitate pentru ${folder}`}
                              value={overrides[folder] ?? ''}
                              onChange={(e) => setOverride(folder, e.target.value)}
                            >
                              <option value="">
                                {p.match.status === 'ambiguous'
                                  ? `Ambiguu: ${p.match.candidates.map((c) => c.cod).join(' / ')}`
                                  : 'Fără corespondență'}
                              </option>
                              {entities.map((en) => (
                                <option key={en.cod} value={en.cod}>
                                  {en.cod} — {en.name}
                                </option>
                              ))}
                            </Select>
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-xs">
                          {p.sheet ? (
                            <span className="inline-flex items-center gap-1 text-slate-600 dark:text-slate-400">
                              <FileSpreadsheet className="h-3.5 w-3.5" />
                              {p.sheet.modifiedTime
                                ? new Date(p.sheet.modifiedTime).toLocaleDateString('ro-MD')
                                : p.sheet.name}
                            </span>
                          ) : matched ? (
                            <span className="text-amber-700">lipsește</span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={!p.sheet || run.isPending}
                            onClick={() => run.mutate({ onlyFolders: [folder] })}
                          >
                            Sincronizează
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </CardBody>
          {Object.keys(overrides).length > 0 ? (
            <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-3 py-2 dark:border-slate-700">
              <span className="text-xs text-slate-500">
                {Object.keys(overrides).length} asocieri nesalvate
              </span>
              <Button
                size="sm"
                onClick={() => saveOverrides.mutate(overrides)}
                loading={saveOverrides.isPending}
              >
                Salvează asocierile
              </Button>
            </div>
          ) : null}
        </Card>
      ) : null}

      {/* Last run detail ---------------------------------------------- */}
      {run.data ? (
        <Card>
          <CardHeader className="px-3 py-2 text-sm font-semibold">Rezultatul rulării</CardHeader>
          <CardBody className="overflow-auto p-0">
            <table className="table-striped w-full text-sm">
              <tbody>
                {run.data.entities.map((e) => (
                  <tr key={e.folder} className="border-t border-slate-100">
                    <td className="px-3 py-1.5 font-mono text-xs">{e.folder}</td>
                    <td className="px-3 py-1.5">
                      <Badge
                        tone={
                          e.status === 'imported'
                            ? 'green'
                            : e.status === 'failed'
                              ? 'red'
                              : 'yellow'
                        }
                      >
                        {e.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400">
                      {e.status === 'imported'
                        ? `${e.imported}/${e.parsed} rânduri (${e.duplicates} deja existente)`
                        : e.reason}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}
